import { useState, type ReactElement } from 'react';
import type { BacklinkDto } from '@gr/core/content/entities/dto';
import { BlogPermission } from '@gr/core/accounts/workspaces/permissions';
import { Link, useNavigate, useServices, useT } from '../../../app/di';
import { useAction, useResource } from '../../../shared/hooks';
import { Alert } from '../../../ui-kit/Alert/Alert';
import { Button } from '../../../ui-kit/Button/Button';
import { Card } from '../../../ui-kit/Card/Card';
import { useConfirm } from '../../../ui-kit/ConfirmDialog/ConfirmDialog';
import { Loading } from '../../../ui-kit/Loading/Loading';
import { PageHeader } from '../../../ui-kit/PageHeader/PageHeader';
import { useBlog } from '../../accounts/workspaces';
import { BacklinkList } from './BacklinkList/BacklinkList';
import { EntityRecordForm } from './EntityRecordForm/EntityRecordForm';
import { useReferenceSearch } from './use-reference-search';

// ENT-UC-5, ENT-UC-7, ENT-UC-9, ENT-RULE-1, ENT-UC-9.CC-1
export function EntityPage({ typeId, entityId }: { typeId: string; entityId: string }): ReactElement {
  const { api } = useServices();
  const { t, te } = useT();
  const { blog, can } = useBlog();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const isNew = entityId === 'new';
  const editable = can(BlogPermission.EditContent);
  const type = useResource(() => api.entities.type(blog.id, typeId), [api, blog.id, typeId]);
  const entity = useResource(
    () => (isNew ? Promise.resolve({ success: true, data: null, messages: [] }) : api.entities.get(blog.id, entityId)),
    [api, blog.id, entityId, isNew],
  );
  const references = useReferenceSearch(blog.id, entity.data?.referenceTitles);
  const remove = useAction(() => api.entities.delete(blog.id, entityId));
  const [blockers, setBlockers] = useState<BacklinkDto[]>([]);

  if (type.loading || entity.loading) return <Loading label={t('common.loading')} />;
  if (type.error || entity.error || !type.data) return <Alert tone="danger">{te(type.error ?? entity.error)}</Alert>;
  const title = isNew ? t('entities.card.newTitle', { type: type.data.name }) : (entity.data?.title ?? '');

  const onDelete = async (): Promise<void> => {
    if (!(await confirm({ title: t('entities.card.deleteConfirm', { title }), confirmLabel: t('common.delete'), danger: true }))) return;
    const result = await remove.run();
    if (result.success) navigate('entities', { blogId: blog.id, typeId });
    else setBlockers(result.data ?? []);
  };

  return (
    <div className="u-stack-lg">
      <PageHeader
        title={title}
        subtitle={
          <Link to="entities" params={{ blogId: blog.id, typeId }}>
            ← {type.data.pluralName}
          </Link>
        }
        actions={
          !isNew && editable ? (
            <Button variant="ghost" icon="trash" loading={remove.pending} onClick={() => void onDelete()}>
              {t('common.delete')}
            </Button>
          ) : null
        }
      />
      {remove.error ? (
        <Alert tone="danger" title={te(remove.error)}>
          {blockers.length > 0 ? <BacklinkList blogId={blog.id} items={blockers} /> : null}
        </Alert>
      ) : null}
      <Card>
        <EntityRecordForm
          fields={type.data.fields}
          initial={entity.data?.values ?? {}}
          references={references}
          editable={editable}
          isNew={isNew}
          onSubmit={async (values) => {
            const result = isNew ? await api.entities.create(blog.id, typeId, values) : await api.entities.update(blog.id, entityId, values);
            if (result.success) {
              if (isNew) navigate('entity', { blogId: blog.id, typeId, entityId: result.data.id }, { replace: true });
              else entity.setData(result.data);
            }
            return result;
          }}
        />
      </Card>
      {!isNew && entity.data ? (
        <Card title={t('entities.card.backlinks')}>
          <BacklinkList blogId={blog.id} items={entity.data.backlinks} />
        </Card>
      ) : null}
    </div>
  );
}
